import { Container, Graphics, Text } from 'pixi.js'
import gsap from 'gsap'
import { delay } from '../../framework/core/async'
import type { Orientation, Resizable } from '../../framework/render/design'
import { NumberCounter } from '../view/NumberCounter'

// 特色玩法表現層 — FreeSpins 進/出場、Jackpot、MaxWin 的橫幅 + 金額計數。
// 對應 Unity FreeSpinsUI / JackpotUI / MaxWinUI / RewardPresentationPanel。
export class FeatureOverlay extends Container implements Resizable {
  private readonly dim = new Graphics()
  private readonly title: Text
  private readonly subtitle: Text
  private readonly counter = new NumberCounter()
  private width0 = 0
  private height0 = 0

  constructor() {
    super()
    this.visible = false
    this.title = new Text({
      text: '',
      style: { fill: 0xffd24a, fontWeight: 'bold', fontFamily: 'Arial', fontSize: 80 },
    })
    this.title.anchor.set(0.5)
    this.subtitle = new Text({
      text: '',
      style: { fill: 0xffffff, fontWeight: 'bold', fontFamily: 'Arial', fontSize: 44 },
    })
    this.subtitle.anchor.set(0.5)
    this.addChild(this.dim, this.title, this.subtitle, this.counter)
  }

  resize(width: number, height: number, _orientation: Orientation): void {
    this.width0 = width
    this.height0 = height
    this.layout()
  }

  private layout(): void {
    const w = this.width0
    const h = this.height0
    this.dim.clear()
    this.dim.rect(0, 0, w, h).fill({ color: 0x000000, alpha: 0.6 })
    const cx = w / 2
    const cy = h * 0.42
    const base = Math.min(w, h)
    this.title.position.set(cx, cy - base * 0.12)
    this.title.style.fontSize = Math.max(32, base * 0.1)
    this.subtitle.position.set(cx, cy)
    this.subtitle.style.fontSize = Math.max(20, base * 0.06)
    this.counter.position.set(cx, cy + base * 0.12)
    this.counter.style.fontSize = Math.max(24, base * 0.08)
  }

  async showFreeSpinsIntro(count: number): Promise<void> {
    await this.present('FREE SPINS', `x ${count}`, null, 1600)
  }

  async showFreeSpinsOutro(total: number): Promise<void> {
    await this.present('恭喜獲得', '', total, 1800)
  }

  async showJackpot(tier: string, amount: number): Promise<void> {
    await this.present(`${tier} JACKPOT`, '', amount, 2200)
  }

  async showMaxWin(amount: number): Promise<void> {
    await this.present('MAX WIN', '', amount, 2200)
  }

  private async present(
    title: string,
    subtitle: string,
    amount: number | null,
    holdMs: number,
  ): Promise<void> {
    this.layout()
    this.visible = true
    this.title.text = title
    this.subtitle.text = subtitle
    this.subtitle.visible = subtitle.length > 0
    this.counter.visible = amount !== null

    this.title.alpha = 0
    this.title.scale.set(0.4)
    await new Promise<void>((resolve) => {
      gsap.to(this.title, { alpha: 1, duration: 0.3 })
      gsap.to(this.title.scale, { x: 1, y: 1, duration: 0.6, ease: 'back.out(2)', onComplete: () => resolve() })
    })

    if (amount !== null) {
      this.counter.setValue(0)
      await this.counter.countTo(amount, 1.2)
    }
    await delay(holdMs)
    this.visible = false
    this.counter.killTween()
  }
}
